import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, Clock, MapPin, Users, Wine, ChevronLeft, ChevronRight, 
  X, CreditCard, Check, Heart, Utensils, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FooterSection } from "../components/landing/FooterSection";

// 이벤트 데이터 생성
const generateEvents = () => {
  const events = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // 앞으로 3개월간의 이벤트 생성
  for (let monthOffset = 0; monthOffset < 3; monthOffset++) {
    const year = currentMonth + monthOffset > 11 ? currentYear + 1 : currentYear;
    const month = (currentMonth + monthOffset) % 12;
    
    // 해당 월의 모든 금요일 찾기
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    let fridayCount = 0;
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      if (date.getDay() === 5) { // 금요일
        fridayCount++;
        if (fridayCount <= 3) { // 첫째주, 둘째주, 셋째주만
          const ageGroups = ["40대", "50대", "60대"];
          const ageGroup = ageGroups[fridayCount - 1];
          
          // 첫째주(40대)는 마감 확률 높게 설정 (데모용)
          const spotsLeft = fridayCount === 1 ? 
            (Math.random() < 0.6 ? 0 : Math.floor(Math.random() * 3) + 1) : 
            Math.floor(Math.random() * 6) + 1;
          
          events.push({
            id: `${year}-${month + 1}-${day}`,
            date: new Date(year, month, day),
            day: day,
            month: month,
            year: year,
            weekOfMonth: fridayCount,
            ageGroup: ageGroup,
            title: `${ageGroup} 소그룹 미팅`,
            time: "저녁 6시",
            location: "강남 와인바",
            price: 300000,
            description: `${ageGroup} 남녀 각 6명이 함께하는 프리미엄 와인 미팅입니다. 고급 와인과 코스 요리를 즐기며 자연스럽게 대화를 나눕니다.`,
            includes: ["프리미엄 와인 무제한", "코스 요리", "전문 MC 진행", "매칭 서비스"],
            spotsLeft: spotsLeft,
          });
        }
      }
    }
  }
  
  return events;
};

export default function GroupMeetingPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [showSoldOutModal, setShowSoldOutModal] = useState(false);
  
  const events = useMemo(() => generateEvents(), []);
  
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // 달력 데이터 생성
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    
    const days = [];
    
    // 이전 달의 날짜들
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(currentYear, currentMonth - 1, prevMonthLastDay - i)
      });
    }
    
    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day: day,
        isCurrentMonth: true,
        date: new Date(currentYear, currentMonth, day)
      });
    }
    
    // 다음 달의 날짜들 (6주 채우기)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day: day,
        isCurrentMonth: false,
        date: new Date(currentYear, currentMonth + 1, day)
      });
    }
    
    return days;
  }, [currentYear, currentMonth]);
  
  const getEventForDay = (day, month, year) => {
    return events.find(e => 
      e.day === day && 
      e.month === month && 
      e.year === year
    );
  };
  
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  const monthNames = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  
  const getAgeGroupColor = (ageGroup) => {
    switch (ageGroup) {
      case "40대": return "bg-emerald-500";
      case "50대": return "bg-blue-500";
      case "60대": return "bg-purple-500";
      default: return "bg-burgundy-500";
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_6d8c2513-f84b-4244-9ad3-aff494435f58/artifacts/fwknu4o1_ChatGPT%20Image%20Mar%2024%2C%202026%2C%2010_23_52%20PM.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 15%',
          }}
        />
        {/* Dark Overlay - 40% brighter */}
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/50 via-stone-800/30 to-burgundy-900/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-transparent to-stone-900/20" />
        
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gold-400/20 border border-gold-400/30 rounded-full text-gold-300 text-sm font-medium mb-6">
              <Wine className="w-4 h-4" />
              Premium Wine Meeting
            </span>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-[720] text-white mb-6">
              6:6 소그룹
              <span className="block text-gold-400 mt-2">오프라인 미팅</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
              매주 금요일 저녁, 강남 와인바에서<br />
              같은 연령대의 6명과 특별한 만남을 가져보세요
            </p>

            {/* Info Cards */}
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              {[
                { icon: Calendar, label: "매주 금요일", value: "저녁 6시 (KST)" },
                { icon: MapPin, label: "장소", value: "강남 와인바" },
                { icon: Users, label: "인원", value: "남녀 각 6명" },
                { icon: Wine, label: "참가비", value: "30만원" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20"
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5 text-gold-400" />
                    <div className="text-left">
                      <p className="text-white/60 text-xs">{item.label}</p>
                      <p className="text-white font-semibold">{item.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Schedule Info */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { week: "첫째 주", ageGroup: "40대", color: "bg-emerald-500", desc: "활기찬 40대의 만남" },
              { week: "둘째 주", ageGroup: "50대", color: "bg-blue-500", desc: "성숙한 50대의 만남" },
              { week: "셋째 주", ageGroup: "60대", color: "bg-purple-500", desc: "여유로운 60대의 만남" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl bg-stone-50"
              >
                <div className={`w-16 h-16 ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <span className="text-white font-bold text-xl">{item.ageGroup}</span>
                </div>
                <h3 className="font-bold text-stone-800 text-lg mb-1">{item.week} 금요일</h3>
                <p className="text-stone-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Meeting - 부연설명 */}
      <section className="py-16 bg-gradient-to-br from-stone-100 to-stone-200">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-burgundy-500 font-medium text-sm tracking-wider uppercase">About</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              6:6 소그룹 미팅이란?
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-stone-200"
          >
            <div className="space-y-6 text-stone-700 leading-relaxed">
              <p className="text-lg">
                <span className="text-burgundy-600 font-semibold">6:6 소그룹 오프라인 미팅</span>은 
                같은 연령대의 남녀 각 6명이 강남의 프라이빗한 와인바에서 만나 
                고급 와인과 코스 요리를 즐기며 자연스럽게 대화를 나누는 프리미엄 미팅입니다.
              </p>
              
              <p>
                전문 MC의 진행으로 어색함 없이 모든 참가자와 대화할 수 있는 시간이 주어지며,
                미팅 후에는 마음에 드는 분과의 <span className="text-burgundy-600 font-semibold">1:1 매칭 서비스</span>도 제공됩니다.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
                  <h4 className="text-burgundy-600 font-bold mb-2">📍 장소</h4>
                  <p className="text-sm text-stone-600">강남역 인근 프라이빗 와인바<br/>(상세 주소는 신청 후 안내)</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
                  <h4 className="text-burgundy-600 font-bold mb-2">⏰ 진행 시간</h4>
                  <p className="text-sm text-stone-600">약 2시간 30분 ~ 3시간<br/>(저녁 6시 시작, 한국시간 기준)</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
                  <h4 className="text-burgundy-600 font-bold mb-2">👔 드레스 코드</h4>
                  <p className="text-sm text-stone-600">세미 포멀 / 스마트 캐주얼<br/>(단정한 복장 권장)</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
                  <h4 className="text-burgundy-600 font-bold mb-2">💝 매칭 방식</h4>
                  <p className="text-sm text-stone-600">미팅 후 선호 이성 선택<br/>상호 선택 시 연락처 교환</p>
                </div>
              </div>

              <div className="bg-burgundy-50 border border-burgundy-200 rounded-xl p-5 mt-6">
                <p className="text-burgundy-700 text-sm">
                  <strong>💡 참고:</strong> 참가비 30만원에는 프리미엄 와인(무제한), 코스 요리, 
                  전문 MC 진행비, 장소 대관료, 매칭 서비스가 모두 포함되어 있습니다.
                  추가 비용은 일절 없습니다.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-16 bg-stone-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">Schedule</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2">
              미팅 일정
            </h2>
          </motion.div>

          {/* Calendar */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {/* Calendar Header */}
            <div className="bg-gradient-to-r from-burgundy-600 to-burgundy-700 p-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPrevMonth}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <h3 className="text-2xl font-bold text-white">
                  {currentYear}년 {monthNames[currentMonth]}
                </h3>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Day Names */}
            <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
              {dayNames.map((day, index) => (
                <div
                  key={day}
                  className={`py-3 text-center text-sm font-semibold ${
                    index === 0 ? "text-red-500" : index === 6 ? "text-blue-500" : "text-stone-600"
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {calendarDays.map((dayInfo, index) => {
                const event = dayInfo.isCurrentMonth 
                  ? getEventForDay(dayInfo.day, currentMonth, currentYear) 
                  : null;
                const isToday = dayInfo.isCurrentMonth && 
                  dayInfo.day === new Date().getDate() && 
                  currentMonth === new Date().getMonth() &&
                  currentYear === new Date().getFullYear();
                
                return (
                  <div
                    key={index}
                    className={`min-h-[100px] p-2 border-b border-r border-stone-100 ${
                      !dayInfo.isCurrentMonth ? "bg-stone-50" : "bg-white"
                    } ${event ? "cursor-pointer hover:bg-stone-50 transition-colors" : ""}`}
                    onClick={() => event && setSelectedEvent(event)}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      !dayInfo.isCurrentMonth ? "text-stone-300" : 
                      index % 7 === 0 ? "text-red-500" : 
                      index % 7 === 6 ? "text-blue-500" : "text-stone-700"
                    }`}>
                      <span className={`${isToday ? "bg-burgundy-500 text-white w-7 h-7 rounded-full inline-flex items-center justify-center" : ""}`}>
                        {dayInfo.day}
                      </span>
                    </div>
                    
                    {event && (
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className={`${event.spotsLeft > 0 ? getAgeGroupColor(event.ageGroup) : 'bg-stone-400'} rounded-lg p-2 text-white text-xs relative`}
                      >
                        <p className="font-bold">{event.ageGroup} 미팅</p>
                        <p className="opacity-80">{event.spotsLeft > 0 ? event.time : '마감'}</p>
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-6 mt-6">
            {[
              { color: "bg-emerald-500", label: "40대" },
              { color: "bg-blue-500", label: "50대" },
              { color: "bg-purple-500", label: "60대" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-4 h-4 ${item.color} rounded`}></div>
                <span className="text-sm text-stone-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-gold-500 font-medium text-sm tracking-wider uppercase">What's Included</span>
            <h2 className="text-3xl md:text-4xl font-sans font-[720] text-stone-800 mt-2 mb-4">
              참가비 30만원에 포함된 내용
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Wine, title: "프리미엄 와인", desc: "엄선된 와인 무제한" },
              { icon: Utensils, title: "코스 요리", desc: "풀코스 디너 제공" },
              { icon: Users, title: "전문 MC", desc: "자연스러운 분위기 연출" },
              { icon: Heart, title: "매칭 서비스", desc: "마음에 드는 분과 연결" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-stone-50 rounded-2xl"
              >
                <div className="w-14 h-14 bg-burgundy-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-burgundy-500" />
                </div>
                <h3 className="font-bold text-stone-800 mb-1">{item.title}</h3>
                <p className="text-sm text-stone-500">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FooterSection />

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && !showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`${getAgeGroupColor(selectedEvent.ageGroup)} p-6 text-white relative`}>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Wine className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{selectedEvent.title}</h3>
                    <p className="text-white/80">
                      {selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일 (금)
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-stone-400" />
                    <span className="text-stone-700">{selectedEvent.time}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-stone-400" />
                    <span className="text-stone-700">{selectedEvent.location}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-stone-400" />
                    {selectedEvent.spotsLeft > 0 ? (
                      <span className="text-stone-700">남녀 각 6명 (잔여 {selectedEvent.spotsLeft}석)</span>
                    ) : (
                      <span className="text-red-500 font-semibold">마감 (잔여 0석)</span>
                    )}
                  </div>
                </div>

                <p className="text-stone-600 mb-6">{selectedEvent.description}</p>

                <div className="bg-stone-50 rounded-xl p-4 mb-6">
                  <h4 className="font-semibold text-stone-800 mb-3">포함 내용</h4>
                  <ul className="space-y-2">
                    {selectedEvent.includes.map((item, index) => (
                      <li key={index} className="flex items-center gap-2 text-stone-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                  <div>
                    <p className="text-sm text-stone-500">참가비</p>
                    <p className="text-2xl font-bold text-stone-800">₩{selectedEvent.price.toLocaleString()}</p>
                  </div>
                  {selectedEvent.spotsLeft > 0 ? (
                    <Button
                      onClick={() => setShowPaymentModal(true)}
                      className={`${getAgeGroupColor(selectedEvent.ageGroup)} hover:opacity-90 text-white px-6 py-3 rounded-xl font-semibold`}
                    >
                      신청하기
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowSoldOutModal(true)}
                      className="bg-stone-400 hover:bg-stone-500 text-white px-6 py-3 rounded-xl font-semibold cursor-not-allowed"
                    >
                      마감
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => {
              setShowPaymentModal(false);
              setPaymentComplete(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {!paymentComplete ? (
                <>
                  <div className="bg-gradient-to-r from-stone-800 to-stone-900 p-6 text-white">
                    <button
                      onClick={() => setShowPaymentModal(false)}
                      className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <CreditCard className="w-10 h-10 mb-3" />
                    <h3 className="text-2xl font-bold">결제하기</h3>
                    <p className="text-white/80 text-sm">{selectedEvent.title}</p>
                  </div>

                  <div className="p-6">
                    <div className="bg-stone-100 rounded-xl p-4 mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-stone-600">일시</span>
                        <span className="font-medium text-stone-800">
                          {selectedEvent.month + 1}/{selectedEvent.day} (금) {selectedEvent.time}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-stone-600">참가비</span>
                        <span className="text-xl font-bold text-stone-800">₩{selectedEvent.price.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">이름</label>
                        <input
                          type="text"
                          placeholder="홍길동"
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">연락처</label>
                        <input
                          type="tel"
                          placeholder="010-1234-5678"
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-2">카드 번호</label>
                        <input
                          type="text"
                          placeholder="1234 5678 9012 3456"
                          className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">유효기간</label>
                          <input
                            type="text"
                            placeholder="MM/YY"
                            className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-2">CVC</label>
                          <input
                            type="text"
                            placeholder="123"
                            className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                          />
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => setPaymentComplete(true)}
                      className={`w-full py-6 ${getAgeGroupColor(selectedEvent.ageGroup)} hover:opacity-90 text-white font-bold text-lg rounded-xl`}
                    >
                      ₩{selectedEvent.price.toLocaleString()} 결제하기
                    </Button>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-emerald-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-800 mb-2">결제 완료!</h3>
                  <p className="text-stone-600 mb-6">
                    {selectedEvent.title}에 성공적으로 신청되었습니다.<br />
                    상세 안내는 문자로 발송됩니다.
                  </p>
                  <div className="bg-stone-100 rounded-xl p-4 mb-6 text-left">
                    <p className="text-sm text-stone-500 mb-1">일시</p>
                    <p className="font-semibold text-stone-800 mb-3">
                      {selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일 (금) {selectedEvent.time}
                    </p>
                    <p className="text-sm text-stone-500 mb-1">장소</p>
                    <p className="font-semibold text-stone-800">{selectedEvent.location}</p>
                  </div>
                  <Button
                    onClick={() => {
                      setShowPaymentModal(false);
                      setSelectedEvent(null);
                      setPaymentComplete(false);
                    }}
                    className="w-full py-4 bg-stone-800 hover:bg-stone-900 text-white rounded-xl"
                  >
                    확인
                  </Button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sold Out Modal */}
      <AnimatePresence>
        {showSoldOutModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setShowSoldOutModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <X className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold text-stone-800 mb-2">신청 마감</h3>
                <p className="text-stone-600 mb-6">
                  죄송합니다. <span className="font-semibold">{selectedEvent.title}</span>의<br />
                  신청 인원이 마감되었습니다.
                </p>
                
                <div className="bg-stone-100 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-stone-500 mb-1">마감된 일정</p>
                  <p className="font-semibold text-stone-800">
                    {selectedEvent.year}년 {selectedEvent.month + 1}월 {selectedEvent.day}일 (금) {selectedEvent.time}
                  </p>
                </div>

                <div className="bg-gold-50 border border-gold-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gold-800">
                    <strong>💡 TIP:</strong> 다른 주차의 미팅을 확인해보세요!<br />
                    또는 다음 달 일정을 미리 신청하실 수 있습니다.
                  </p>
                </div>

                <Button
                  onClick={() => {
                    setShowSoldOutModal(false);
                    setSelectedEvent(null);
                  }}
                  className="w-full py-4 bg-stone-800 hover:bg-stone-900 text-white rounded-xl"
                >
                  다른 일정 보기
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
